import React, { useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import moment from 'moment';
import cogoToast from 'cogo-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';

import { saveRoutine, getRoutineVersions, activateRoutineVersion } from '../../api/spotlights';
import './RoutineEditorModal.scss';

// Editor for a single automation routine — the versioned, code-free config the
// ad-automation extension consumes. Saving creates a NEW validated version and
// activates it (the backend rejects invalid config). Older versions are listed
// for one-click rollback. Pass a `routine` to edit, or null to create a new one.
//
// steps/selectors/subroutines/scroll are edited as raw JSON (same shapes the
// extension's popup editor and the validator expect) — we parse + surface errors
// locally before hitting the server so a typo doesn't round-trip.
const JSON_FIELDS = [
  { key: 'steps', label: 'Steps (JSON array)', required: true },
  { key: 'selectors', label: 'Selectors (JSON object)', required: true },
  { key: 'subroutines', label: 'Subroutines (JSON, optional)', required: false },
  { key: 'scroll', label: 'Scroll config (JSON, optional)', required: false }
];

const toText = v => (v == null ? '' : JSON.stringify(v, null, 2));

const RoutineEditorModal = ({ routine, onClose, onSaved }) => {
  const isNew = !routine;
  const [platform, setPlatform] = useState(routine?.platform || 'meta');
  const [routineKey, setRoutineKey] = useState(routine?.routineKey || '');
  const [label, setLabel] = useState(routine?.label || '');
  const [notes, setNotes] = useState(routine?.notes || '');
  const [json, setJson] = useState({
    steps: toText(routine?.steps),
    selectors: toText(routine?.selectors),
    subroutines: toText(routine?.subroutines),
    scroll: toText(routine?.scroll)
  });
  const [fieldError, setFieldError] = useState({});
  const [saving, setSaving] = useState(false);

  const [versions, setVersions] = useState(null);
  const [showVersions, setShowVersions] = useState(false);
  const [activatingId, setActivatingId] = useState(null);

  const loadVersions = useCallback(() => {
    if (isNew || !routineKey) return;
    getRoutineVersions(platform, routineKey)
      .then(r => setVersions(r.versions || []))
      .catch(() => setVersions([]));
  }, [isNew, platform, routineKey]);

  useEffect(() => {
    if (showVersions && versions == null) loadVersions();
  }, [showVersions, versions, loadVersions]);

  // Parse a JSON field, recording a per-field error. Empty optional field → null.
  const parseField = (key, required) => {
    const text = (json[key] || '').trim();
    if (!text) {
      if (required) throw new Error(`${key} is required.`);
      return null;
    }
    try {
      return JSON.parse(text);
    } catch (e) {
      throw new Error(`${key}: ${e.message}`);
    }
  };

  const submit = async () => {
    if (!routineKey.trim()) return cogoToast.error('Routine key is required.');
    const errors = {};
    const parsed = {};
    for (const { key, required } of JSON_FIELDS) {
      try {
        parsed[key] = parseField(key, required);
      } catch (e) {
        errors[key] = e.message;
      }
    }
    setFieldError(errors);
    if (Object.keys(errors).length) return cogoToast.error('Fix the highlighted JSON before saving.');

    setSaving(true);
    try {
      const r = await saveRoutine({
        platform: platform.trim() || 'meta',
        routineKey: routineKey.trim(),
        label: label.trim() || null,
        notes: notes.trim() || null,
        steps: parsed.steps,
        selectors: parsed.selectors,
        subroutines: parsed.subroutines,
        scroll: parsed.scroll,
        activate: true
      });
      cogoToast.success(`Saved ${r.routine.platform}/${r.routine.routineKey} v${r.routine.version} (live).`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Failed to save routine.');
    } finally {
      setSaving(false);
    }
  };

  const activate = async v => {
    setActivatingId(v.id);
    try {
      await activateRoutineVersion(v.id);
      cogoToast.success(`Activated v${v.version}.`);
      setVersions(null); // force a reload to reflect new active flag
      loadVersions();
      onSaved && onSaved();
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Failed to activate version.');
    } finally {
      setActivatingId(null);
    }
  };

  return (
    <div className='sl-modal-overlay' onMouseDown={onClose}>
      <div className='sl-modal' style={{ maxWidth: 760 }} onMouseDown={e => e.stopPropagation()}>
        <div className='sl-modal-head'>
          <div className='sl-modal-title'>
            {isNew ? 'New automation routine' : `Edit ${routine.platform}/${routine.routineKey}`}
            {!isNew && <span className='sl-pill' style={{ marginLeft: 10 }}>v{routine.version} live</span>}
          </div>
          <button className='sl-modal-close' onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className='sl-modal-body sl-routine-form'>
          <div className='sl-field-row'>
            <label className='sl-field' style={{ flex: '0 0 170px' }}>
              <span>Platform</span>
              <input type='text' value={platform} disabled={!isNew} onChange={e => setPlatform(e.target.value)} placeholder='meta' />
            </label>
            <label className='sl-field' style={{ flex: 1 }}>
              <span>Routine key</span>
              <input type='text' value={routineKey} disabled={!isNew} onChange={e => setRoutineKey(e.target.value)} placeholder='identity_flip_and_unpause' />
            </label>
          </div>

          <label className='sl-field'>
            <span>Label</span>
            <input type='text' value={label} onChange={e => setLabel(e.target.value)} placeholder='Human-friendly name' />
          </label>

          {JSON_FIELDS.map(({ key, label: flabel, required }) => (
            <label key={key} className={cn('sl-field', { 'is-error': fieldError[key] })}>
              <span>
                {flabel}
                {required && <span className='sl-req'> *</span>}
              </span>
              <textarea
                className='sl-code'
                style={{ minHeight: key === 'steps' ? 220 : 120 }}
                spellCheck={false}
                value={json[key]}
                onChange={e => setJson(j => ({ ...j, [key]: e.target.value }))}
              />
              {fieldError[key] && <div className='sl-field-error'>{fieldError[key]}</div>}
            </label>
          ))}

          <label className='sl-field'>
            <span>Notes</span>
            <input type='text' value={notes} onChange={e => setNotes(e.target.value)} placeholder='What changed / why' />
          </label>

          {!isNew && (
            <div style={{ marginTop: 10 }}>
              <button className='sl-btn secondary small' onClick={() => setShowVersions(s => !s)}>
                {showVersions ? 'Hide version history' : 'Version history'}
              </button>
              {showVersions && (
                <div className='sl-vip-results' style={{ marginTop: 10 }}>
                  {versions == null ? (
                    <div className='sl-empty' style={{ padding: 16 }}>Loading…</div>
                  ) : !versions.length ? (
                    <div className='sl-empty' style={{ padding: 16 }}>No versions.</div>
                  ) : (
                    versions.map(v => (
                      <div className='sl-vip-result' key={v.id}>
                        <div>
                          <span style={{ fontWeight: 500 }}>v{v.version}</span>
                          {v.isActive && <span className='sl-pill green' style={{ marginLeft: 8 }}>live</span>}
                          <div className='sub' style={{ color: '#888', fontSize: 11 }}>
                            {v.label || '—'} · {v.updatedAt ? moment(v.updatedAt).format('MMM D, h:mma') : ''}
                          </div>
                        </div>
                        {v.isActive ? (
                          <span className='sl-pill'>current</span>
                        ) : (
                          <button className='sl-btn small secondary' disabled={activatingId === v.id} onClick={() => activate(v)}>
                            {activatingId === v.id ? 'Activating…' : 'Make live'}
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
            <button className='sl-btn secondary' onClick={onClose}>
              Cancel
            </button>
            <button className='sl-btn' disabled={saving} onClick={submit}>
              {saving ? 'Saving…' : 'Save new version'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoutineEditorModal;
